import {
    TableRow,
    Theme,
    createStyles,
    StandardProps,
    Checkbox
} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import React from "react";
import { TableRowProps, TableRowClassKey } from "@material-ui/core/TableRow";
import { darken } from "@material-ui/core/styles/colorManipulator";
import { StylesType, StyleClassKey } from "js/react/typescript";
import AppTableCell from "js/react/components/AppTable/AppTableCell";
import { BulkSelectableProps } from "js/react/HOC";
import clsx from "clsx";

const styles = (theme: Theme) =>
    createStyles({
        root: {
            "&$hover:hover": {
                backgroundColor: theme.palette.grey["200"]
            }
        },
        clickable: {
            cursor: "pointer"
        },
        head: {},
        hover: {},
        altColors: {
            "&:nth-child(2n+1):not($head):not($disabled)": {
                backgroundColor: theme.palette.grey["200"],
                "&$hover:hover": {
                    backgroundColor: darken(theme.palette.common.white, 0.2)
                }
            }
        },
        borderBottom: {
            borderBottom: `1px ${theme.palette.grey["200"]} solid`
        },
        disabled: {
            backgroundColor: theme.palette.grey["A100"]
        },
        darkBorderBottom: {
            borderBottom: `1px #000 solid`
        }
    });

export type AppTableRowClassKey =
    | StyleClassKey<typeof styles>
    | TableRowClassKey;

const decorator = withStyles(styles as StylesType<AppTableRowClassKey>);

export interface AppTableRowProps
    extends BulkSelectableProps,
        StandardProps<TableRowProps, AppTableRowClassKey> {
    alternate?: boolean;
    hasBorderBottom?: boolean;
    hasDarkBorderBottom?: boolean;
    disabled?: boolean;
    clickable?: boolean;
    allowChecked?: boolean;
}

function Component({
    disabled,
    allowChecked,
    clickable,
    alternate,
    hasBorderBottom,
    hasDarkBorderBottom,
    classes,
    className,
    checked = false,
    onCheckedChange,
    children,
    ...rest
}: AppTableRowProps) {
    const {
        altColors,
        clickable: clickableClass,
        disabled: disClass,
        borderBottom,
        darkBorderBottom,
        ...restClasses
    } = classes;

    if (allowChecked) {
        children = (
            <React.Fragment>
                <AppTableCell>
                    <Checkbox
                        checked={checked}
                        onClick={e => e.stopPropagation()}
                        onChange={(event: object, checked: boolean) =>
                            onCheckedChange(checked)
                        }
                    ></Checkbox>
                </AppTableCell>
                {children}
            </React.Fragment>
        );
    }

    return (
        <TableRow
            className={clsx(className, {
                [clickableClass]: clickable,
                [disClass]: disabled,
                [altColors]: alternate,
                [borderBottom]: hasBorderBottom,
                [darkBorderBottom]: hasDarkBorderBottom
            })}
            classes={restClasses}
            {...rest}
        >
            {children}
        </TableRow>
    );
}

Component.defaultProps = {
    disabled: false,
    clickable: false,
    checked: false,
    allowChecked: false,
    alternate: false,
    hasBorderBottom: true,
    hasDarkBorderBottom: false
};

Component.displayName = "AppTableRow";

export const AppTableRow = decorator(Component);

export default AppTableRow;
